import { useEffect, useState } from 'react';
import { BOOK1_STORY } from './data/story';
import { targetLayer, targetUv } from './art/anchors';
import { figuresFor } from './art/figures';
import { TitleScreen } from './components/TitleScreen';
import { GameScreen } from './components/GameScreen';
import { RecapScreen } from './components/RecapScreen';
import { ChapterIntro } from './components/ChapterIntro';
import { Prologue } from './components/Prologue';
import { SceneStage } from './components/SceneStage';
import { useGame } from './hooks/useGame';
import { endingTarget, loadMeta } from '@plotjack/core';
import { webStorageAdapter } from './adapters/localStorage';
import { webAudioAdapter } from './adapters/audio';
import { StartPrompt } from './components/StartPrompt';
import { AvControls } from './components/AvControls';
import { audio } from './audio/engine';
import { getPrefs, useAvPrefs } from './audio/prefs';

/** QA only: ?stage=<sceneId>&mood=<mood> renders one painted scene full screen (pair with ?look=x,y). */
function StagePreview({ sceneId }: { sceneId: string }) {
  const q = new URLSearchParams(window.location.search);
  const mood = q.get('mood') ?? 'calm';
  // ?pin=<storySceneId>:<elementId> shows that target's pin, for checking anchors under tilt
  const [pinScene, pinEl] = (q.get('pin') ?? '').split(':');
  const el = BOOK1_STORY.scenes.find((s) => s.id === pinScene)?.elements.find((e) => e.id === pinEl);
  // ?scene=<storySceneId> stands that scene's painted figures in the preview
  const figures = figuresFor(q.get('scene') ?? pinScene ?? '');
  const pin = el ? { uv: targetUv(pinScene, el.id, el.beatType), label: el.label, hit: false, layer: targetLayer(pinScene, el.id) } : undefined;
  return (
    <div className="stage-preview">
      <SceneStage sceneId={sceneId} mood={mood} glitch={false} showpiece={sceneId === 'title_keyart'} pin={pin} figures={figures} />
    </div>
  );
}

export default function App() {
  const stage = new URLSearchParams(window.location.search).get('stage');
  if (stage) return <StagePreview sceneId={stage} />;
  return <Game />;
}

function Game() {
  const game = useGame();
  const [hasContinue, setHasContinue] = useState(false);
  const prefs = useAvPrefs();
  // QA and embeds can skip the first-visit prompt with ?noprompt
  const [prompt, setPrompt] = useState(() => !getPrefs().decided && !new URLSearchParams(window.location.search).has('noprompt'));

  // A returning player who chose sound gets it back on their first tap (browsers need a gesture).
  useEffect(() => {
    if (!prefs.sound || audio.unlocked) return;
    const go = () => audio.unlock();
    window.addEventListener('pointerdown', go, { once: true, capture: true });
    window.addEventListener('keydown', go, { once: true, capture: true });
    return () => {
      window.removeEventListener('pointerdown', go, { capture: true });
      window.removeEventListener('keydown', go, { capture: true });
    };
  }, [prefs.sound]);

  // Soft click on every interface button (cards and the sound controls make their own sounds).
  useEffect(() => {
    const click = (e: MouseEvent) => {
      const b = (e.target as HTMLElement | null)?.closest?.('button');
      if (b && !b.closest('.av-controls, .card-hand-keys, .start-prompt')) audio.playSfx('ui_click', { volume: 0.5 });
    };
    document.addEventListener('click', click, true);
    return () => document.removeEventListener('click', click, true);
  }, []);

  // Music follows the screen and the scene mood.
  const sceneMood = game.state?.sceneMood;
  useEffect(() => {
    if (game.screen === 'title' || (game.screen === 'game' && game.showPrologue)) webAudioAdapter.setMusicMood?.('title');
    else if (game.screen === 'recap') webAudioAdapter.setMusicMood?.('ending');
    else if (game.screen === 'game') webAudioAdapter.setMusicMood?.(sceneMood ?? 'calm');
  }, [game.screen, game.showPrologue, sceneMood]);

  useEffect(() => {
    void loadMeta(webStorageAdapter).then((m) => setHasContinue(Boolean(m.continueRun)));
  }, [game.screen]);

  const chapterForIntro =
    game.state?.phase === 'chapter_intro' && game.state.pendingChapterId
      ? BOOK1_STORY.chapters?.find((c) => c.id === game.state!.pendingChapterId) ??
        game.activeChapter
      : game.activeChapter;

  return (
    <div className="app-root">
      <a className="skip-link" href="#main">Skip to content</a>
      <AvControls />
      {prompt && <StartPrompt onClose={() => setPrompt(false)} />}
      <main id="main" className="main">
        {game.screen === 'title' && (
          <TitleScreen
            quest={BOOK1_STORY.quest}
            premise={BOOK1_STORY.premise}
            endings={BOOK1_STORY.endings}
            chapters={BOOK1_STORY.chapters ?? []}
            chosenEnding={game.chosenEnding}
            onChooseEnding={game.setChosenEnding}
            runScope={game.runScope}
            onRunScope={game.setRunScope}
            chapterId={game.chapterId}
            onChapterId={game.setChapterId}
            chaptersCompleted={game.chaptersCompleted}
            hasContinue={hasContinue}
            onStart={() => void game.startRun()}
            onContinue={() => void game.startRun({ continueSaved: true })}
            onHowToPlay={() => void game.startTutorialRun()}
            memories={game.memories}
          />
        )}
        {game.screen === 'game' && game.showPrologue && (
          <Prologue beats={BOOK1_STORY.prologue ?? []} quest={BOOK1_STORY.quest} onDone={game.finishPrologue} />
        )}
        {game.screen === 'game' && !game.showPrologue && game.state?.phase === 'chapter_intro' && chapterForIntro && (
          <ChapterIntro
            chapter={chapterForIntro}
            quest={BOOK1_STORY.quest}
            firstScene={BOOK1_STORY.scenes[chapterForIntro.sceneStart]}
            onContinue={game.beginChapter}
          />
        )}
        {game.screen === 'game' &&
          !game.showPrologue &&
          game.state &&
          game.state.phase !== 'chapter_intro' &&
          game.scene && (
            <GameScreen
              state={game.state}
              scene={game.scene}
              narratorBody={game.narratorBody}
              onBeginScene={game.beginScene}
              onPlayCard={game.onPlayCard}
              onEndScene={game.finishScene}
              onDraft={game.draft}
              tutorialStep={game.tutorialStep}
              tutorialShowNext={game.tutorialShowNext}
              onTutorialNext={game.onTutorialNext}
              onTutorialSkip={() => void game.skipTutorial()}
              onTutorialEvent={game.onTutorialEvent}
            />
          )}
        {game.screen === 'recap' && game.state && (
          <RecapScreen
            ending={BOOK1_STORY.endings.find((e) => e.id === game.state!.endingReached)}
            dread={game.state.dread}
            tracks={game.state.tracks}
            playedCount={(game.state.history ?? []).length}
            log={game.state.sceneLog ?? []}
            chosenEndingId={game.state.chosenEndingId}
            chosenTarget={(() => {
              const c = BOOK1_STORY.endings.find((e) => e.id === game.state!.chosenEndingId);
              return c ? endingTarget(c, game.state!) : undefined;
            })()}
            onTitle={() => game.setScreen('title')}
          />
        )}
      </main>
      <footer className="site-footer">
        <a href="https://threesided.com" target="_blank" rel="noopener noreferrer">
          ThreeSided Studios
        </a>
      </footer>
    </div>
  );
}
